import { Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { teamSizeOptions } from '../../profile/core/_constants'
import TextInput from '../../widgets/components/Input/TextInput'
import { ToolTipUIForModal } from '../../widgets/components/UI/ToolTipUIForModal'
import { industryOptions } from '../../../core/_constants'
import { SelectInput } from '../../widgets/components/Input/SelectInput'
import * as Yup from 'yup'
import { useAuth } from '../../auth'
import { createCompany } from '../../onboarding/core/_requests'
import { companyData, profileData, updateUserDefaultCompany } from '../../profile/core/_requests'
import { toast } from 'react-toastify'
import { getUserByToken, refreshTokenApi } from '../../auth/core/_requests'
import SwitchingCompany from './SwitchingCompany'

const NewCompanyModal = ({ isActive, setIsActive, getAllCompanies, prop }: any) => {
  const { formatMessage } = useIntl()
  const [selectedOption2, setSelectedOption2] = useState<string>('business')
  const [selectedOption, setSelectedOption] = useState<boolean>(false)
  const [isSwitchIsActive, setSwitchIsActive] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const {
    companyId,
    storeCompanyId,
    setCompanyQueuePosition,
    setUseCase,
    setUserToken,
    setInvoiceStatus,
    setPlanStatus,
    setRole,
    saveAuth,
  } = useAuth()
  const [companyProfile, setCompanyProfile] = useState<any>()

  const getProfileInfo = async () => {
    try {
      if (companyId) {
        const {
          data: { success, data, errors },
        } = await profileData(companyId)
        if (success) {
          setCompanyProfile(data)
        } else if (errors) {
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
        }
      }
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    getProfileInfo()
  }, [companyId]) // eslint-disable-line react-hooks/exhaustive-deps

  const initialValues = {
    companyName: '',
    industry: '',
    teamSize: '',
  }

  const manageRecordsSchema = Yup.object().shape({
    companyName: Yup.string().required(formatMessage({ id: 'Company Name is required' })),
    industry: Yup.string().when([], {
      is: () => selectedOption2 === 'business',
      then: (schema) => schema.required(formatMessage({ id: 'Industry is required' })),
      otherwise: (schema) => schema.notRequired(),
    }),
    teamSize: Yup.string().when([], {
      is: () => selectedOption2 === 'business',
      then: (schema) => schema.required(formatMessage({ id: 'Team size is required' })),
      otherwise: (schema) => schema.notRequired(),
    }),
  })

  const refreshToken = async (itemCompany: number) => {
    try {
      const {
        data: { success, data },
      } = await refreshTokenApi()
      if (success) {
        await saveAuth(data)
        setUserToken(data.token)

        if (data?.companyPlanStatus?.invoiceStatus) {
          setInvoiceStatus(data.companyPlanStatus.invoiceStatus)
        }
        if (data?.companyPlanStatus?.planStatus) {
          setPlanStatus(data.companyPlanStatus.planStatus)
        }

        const { data: userData } = await getUserByToken(data.token)
        if (userData?.data) {
          const findCompany = userData?.data?.company?.find(
            (company: { companyId: number }) => company.companyId === itemCompany
          )
          if (findCompany) {
            setCompanyQueuePosition(findCompany?.queuePosition)
            setUseCase(findCompany?.useCase)
            setRole(findCompany?.roles?.roleName?.toLowerCase())
          }
        }
      }
    } catch (error) {
      console.log(error)
    }
  }

  const handleSubmit = async (values: any) => {
    if (!companyProfile?.countryId || !companyProfile?.stateId) {
      toast.error(formatMessage({ id: 'Unable to create company. Missing location details.' }))
      return
    }
    setIsLoading(true)
    const teamSize = selectedOption2 === 'business' ? values.teamSize : '0'
    const industry = selectedOption2 === 'business' ? values.industry : 'Other'
    const forClientUse = selectedOption2 === 'business' ? `${selectedOption}` : 'false'
    try {
      const {
        data: { success, data },
      } = await createCompany(
        values.companyName,
        companyProfile?.countryId!,
        companyProfile?.stateId!,
        companyProfile?.city || 'Manhattan',
        +companyProfile?.zipCode || 10020,
        undefined,
        'Gold-USD-Monthly',
        '',
        selectedOption2 || '',
        forClientUse === 'true',
        Number(teamSize),
        values.industry || industry,
        ''
      )
      if (success) {
        const {
          data: { success },
        }: any = await updateUserDefaultCompany(companyId!, data.companyId)
        if (success) {
          const {
            data: { success, data: value },
          } = await companyData(data.companyId)
          if (success) {
            storeCompanyId(data.companyId)
            setCompanyQueuePosition(data?.queuePosition)
            localStorage.setItem('company', JSON.stringify(value))
            localStorage.setItem('companyId', data.companyId)
            refreshToken(data.companyId)
            if (`${window.location.hostname}` === 'localhost') {
              window.location.href = `${window.location.protocol}//${window.location.hostname}:3003/dashboard`
            } else {
              window.location.href = `${window.location.protocol}//${window.location.hostname}/dashboard`
            }
          } else {
          }
        }
      }
    } catch (err) {
      console.log(err)
      toast.error(formatMessage({ id: 'Something went wrong. Please try again later.' }))
    } finally {
      setIsLoading(false)
      setIsActive(false)
      setSwitchIsActive(true)
    }
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={manageRecordsSchema}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {(formik) => (
          <Form className='w-100'>
            <Modal
              show={isActive}
              onHide={() => {
                setIsActive(false)
                formik.resetForm()
              }}
              size='md'
              {...prop}
            >
              <Modal.Header closeButton>
                <h2>{formatMessage({ id: 'Add New Company' })}</h2>
              </Modal.Header>
              <Modal.Body>
                <div className='container'>
                  <div className='w-100'>
                    <TextInput
                      fieldType='text'
                      label={formatMessage({ id: 'Company Name' })}
                      fieldName={`companyName`}
                      formik={formik}
                      placeholder={formatMessage({ id: 'Enter Company Name' })}
                      toolTipText={formatMessage({
                        id: `Please enter your company's name. This can be the brand name of your company using which it is generally identified publicly.`,
                      })}
                      isStarRequired={true}
                      isTooltipForModal={true}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  <div className='d-flex flex-column gap-4 mb-8'>
                    <label className='d-flex'>
                      <p className='mb-0'>
                        {formatMessage({
                          id: 'Are you signing up for yourself or for your client?*',
                        })}
                        <ToolTipUIForModal tooltipText='Would you like to use RecordsKeeper.AI for managing your own records, or are you setting it up for a client? This will help tailor the experience and recommendations specifically to your needs.' />
                      </p>
                    </label>
                    <div
                      className='d-flex gap-4 align-items-center'
                      onClick={() => setSelectedOption(false)}
                    >
                      <input
                        type='radio'
                        className='form-check-input'
                        checked={!selectedOption}
                        onChange={() => setSelectedOption(false)}
                      />{' '}
                      <div className='d-flex'>
                        <p className='m-0'>
                          {formatMessage({
                            id: `I will use it for myself or my own business`,
                          })}
                          <ToolTipUIForModal tooltipText='Select this option if you are using RecordsKeeper.AI for your own company or personal family records.' />
                        </p>
                      </div>
                    </div>
                    <div
                      className='d-flex gap-4 align-items-center'
                      onClick={() => setSelectedOption(true)}
                    >
                      <input
                        type='radio'
                        className='form-check-input'
                        checked={selectedOption}
                        onChange={() => setSelectedOption(true)}
                      />{' '}
                      <div className='d-flex'>
                        <p className='m-0'>
                          {formatMessage({
                            id: `I will use it on behalf of my clients or customers`,
                          })}
                          <ToolTipUIForModal
                            tooltipText={`Select this option if you are using RecordsKeeper.AI for your client's company or their business needs.`}
                          />
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className='d-flex flex-column gap-4 mb-8'>
                    <label className='d-flex'>
                      <p className='mb-0'>
                        {formatMessage({
                          id: 'How do you plan to revolutionize your record-keeping?*',
                        })}
                        <ToolTipUIForModal tooltipText='Based on your choice, we’ll set up a world-class, AI-powered record structure tailored just for you—so you can stay organized effortlessly from day one!' />
                      </p>
                    </label>
                    <div
                      className='d-flex gap-4 align-items-center'
                      onClick={() => setSelectedOption2('personal')}
                    >
                      <input
                        type='radio'
                        className='form-check-input'
                        checked={selectedOption2 === 'personal'}
                        onChange={() => setSelectedOption2('personal')}
                      />{' '}
                      <div className='d-flex'>
                        <p className='m-0'>
                          {formatMessage({
                            id: `Personal & Family’s Record Keeping`,
                          })}
                          <ToolTipUIForModal tooltipText='Select this option if you are managing records for yourself, your kids, and your family—all in one secure place.' />
                        </p>
                      </div>
                    </div>
                    <div
                      className='d-flex gap-4 align-items-center'
                      onClick={() => setSelectedOption2('business')}
                    >
                      <input
                        type='radio'
                        className='form-check-input'
                        checked={selectedOption2 === 'business'}
                        onChange={() => setSelectedOption2('business')}
                      />{' '}
                      <div className='d-flex'>
                        <p className='m-0'>
                          {formatMessage({
                            id: `Business & Enterprise Record Keeping`,
                          })}
                          <ToolTipUIForModal
                            tooltipText={`Select this option if you are streamlining record-keeping for business with automated organization and compliance support.`}
                          />
                        </p>
                      </div>
                    </div>
                  </div>
                  {selectedOption2 === 'business' && (
                    <>
                      <div className='w-100'>
                        <SelectInput
                          label={formatMessage({ id: 'Which industry does this company belong to?' })}
                          fieldName={'industry'}
                          placeholder={formatMessage({ id: 'Select Industry' })}
                          formik={formik}
                          toolTipText={formatMessage({
                            id: `Your industry determines specific compliance requirements and record management needs. Selecting accurately ensures we can provide relevant templates and automation workflows designed for your sector's regulations.`,
                          })}
                          options={industryOptions}
                          isStarRequired={true}
                          onBlur={formik.handleBlur}
                          isTooltipForModal
                        />
                      </div>
                      <div className='w-100'>
                        <SelectInput
                          label={formatMessage({ id: 'How many people work at this company?' })}
                          fieldName='teamSize'
                          placeholder={formatMessage({
                            id: 'Please select the Source Types',
                          })}
                          formik={formik}
                          options={teamSizeOptions}
                          isStarRequired
                          toolTipText={formatMessage({
                            id: `Team size helps us recommend the right collaboration features and access controls. This information ensures your RecordsKeeper.AI environment scales appropriately with your organization.`,
                          })}
                          isTooltipForModal
                        />
                      </div>
                    </>
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <div className='d-flex flex-md-row flex-column w-100 justify-content-end px-md-0 px-0 gap-3 mt-4'>
                  <button
                    type='button'
                    className='btn bg-light custom-button-property text-muted w-auto order-md-1 order-2'
                    onClick={() => {
                      setIsActive(false)
                      formik.resetForm()
                    }}
                  >
                    {formatMessage({ id: 'Cancel' })}
                  </button>

                  <button
                    type='submit'
                    className='btn text-white bg-primary custom-button-property w-auto order-md-2 order-1'
                    disabled={!formik.isValid || isLoading}
                    onClick={() => handleSubmit(formik.values)}
                  >
                    {!isLoading && (
                      <span className='indicator-label font-size-13 p-0'>
                        {formatMessage({ id: 'Add New Company' })}
                      </span>
                    )}
                    {isLoading && (
                      <span className='indicator-label font-size-13 p-0'>
                        {formatMessage({ id: 'Please wait...' })}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                  <SwitchingCompany isActive={isSwitchIsActive} setIsActive={setSwitchIsActive} />
                </div>
              </Modal.Footer>
            </Modal>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default NewCompanyModal
